// « Nutrition » hub (#/nutrition?r=repas|recettes|courses|journal), refonte navigation 21/10 (spec ARBORESCENCE-ACS.md §3,
// plan Task 4). The rubrique comes from the URL (domains.ts useRubrique) and the local dock (shell) switches it. A recipe page
// opens in place with #/nutrition?r=<rubrique>&recette=<id> (format used by ⌘K): the dock keeps its parent rubrique selected.
// One shared session store for the four rubriques (nutrition-parts.tsx), so a recipe sent to the shopping list is in Courses.
// Previous single-page version kept in archive/before-nutrition-hub-2110/nutrition.tsx.
// Fictitious, neutral demo: no estimate is computed, no calorie or macro value, no medical claim.
import { useLayoutEffect, useRef } from "react";
import { ChevronLeft } from "lucide-react";
import { StatusBar, type Route } from "./parts";
import { HubHead, ShellActions } from "./shell";
import { useHash, useRubrique } from "./domains";
import { RECIPES } from "./nutrition-fixtures";
import { useNutri } from "./nutrition-parts";
import { NutritionRepas } from "./nutrition-repas";
import { NutritionRecettes, RecipeDetail } from "./nutrition-recettes";
import { NutritionCourses } from "./nutrition-courses";
import { NutritionJournal } from "./nutrition-journal";
import "./hub-nutrition.css";

interface NutritionProps { go: (route: Route) => void; onOutOfScope: (label: string) => void }

const RUB_LABEL: Record<string, string> = { repas: "Repas", recettes: "Recettes", courses: "Courses", journal: "Journal" };

export function Nutrition(_props: NutritionProps) {
  const rub = useRubrique("nutrition");
  const hash = useHash();
  const recipeId = hash.match(/[?&]recette=([\w-]+)/)?.[1];
  const recipe = recipeId ? RECIPES.find(r => r.id === recipeId) : undefined;
  const { toast } = useNutri();
  const scroll = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const view = `${rub}|${recipe?.id ?? ""}`;
  const prev = useRef(view);
  // New rubrique or recipe page: back to the top, and a short entrance (reduced motion: opacity only).
  useLayoutEffect(() => {
    if (prev.current === view) return;
    prev.current = view;
    scroll.current?.scrollTo({ top: 0 });
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    body.current?.animate(reduce ? [{ opacity: .5 }, { opacity: 1 }] : [{ opacity: 0, transform: "translateY(8px)" }, { opacity: 1, transform: "none" }],
      { duration: reduce ? 120 : 280, easing: "cubic-bezier(.22, 1, .36, 1)" });
  }, [view]);

  // Back: from a recipe page to its rubrique; from a rubrique (a dock entry) to the home (spec §4).
  const back = recipe ? { hash: `#/nutrition?r=${rub}`, label: `Retour à ${RUB_LABEL[rub] ?? "Nutrition"}` } : { hash: "#/accueil", label: "Retour à l'accueil" };

  return <div className="cc-screen cc-nutrition ccn">
    <StatusBar />
    <div ref={scroll} className="cc-scroll">
      <HubHead domain="nutrition" title={recipe ? "Recette" : undefined} back={back.label} onBack={() => { window.location.hash = back.hash; }} />
      <div ref={body} className="ccn-body">
        {recipe ? <RecipeDetail key={recipe.id} recipe={recipe} />
          : rub === "recettes" ? <NutritionRecettes />
          : rub === "courses" ? <NutritionCourses />
          : rub === "journal" ? <NutritionJournal />
          : <NutritionRepas />}
      </div>
    </div>
    {toast && <div className="cc-nutrition-toast ccn-toast" role="status">{toast}</div>}
  </div>;
}
